import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { api, errMessage } from "../lib/api";
import { track } from "../lib/analytics";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
    />
  </svg>
);

export default function AuthPage() {
  const { login, register, loginWithGoogle } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab =
    searchParams.get("mode") === "register" ? "register" : "login";

  const [loading, setLoading] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlist, setWaitlist] = useState({
    district_name: "",
    contact_name: "",
    contact_email: "",
  });
  const [waitlistBusy, setWaitlistBusy] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setWaitlistBusy(true);
    try {
      const { data } = await api.post("/sso/waitlist", waitlist);
      setWaitlistDone(true);
      toast.success(
        data.already
          ? "You're already on the waitlist — we'll be in touch!"
          : "You're on the district waitlist!",
      );
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setWaitlistBusy(false);
    }
  };

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({
    name: "",
    email: "",
    password: "",
    attestation: false,
    schoolName: "",
    accountType: [
      "professional",
      "teacher",
      "school_admin",
      "creator",
    ].includes(searchParams.get("type"))
      ? searchParams.get("type")
      : "parent",
  });

  const afterAuth = (user) => {
    if (user.role === "professional") {
      navigate("/pro");
      return;
    }
    if (user.role === "teacher" || user.role === "school_admin") {
      navigate("/school");
      return;
    }
    if (user.role === "creator") {
      navigate("/creator");
      return;
    }
    const goDashboard =
      user.onboarding_complete || user.platform_role === "platform_admin";
    navigate(goDashboard ? "/dashboard" : "/onboarding");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      track("login", { method: "password" });
      afterAuth(user);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regForm.attestation) {
      toast.error("Please confirm you are an adult parent or guardian");
      return;
    }
    setLoading(true);
    track("sign_up_started", { method: "password" });
    try {
      const user = await register(
        regForm.name,
        regForm.email,
        regForm.password,
        regForm.attestation,
        regForm.accountType,
        regForm.accountType === "school_admin" ? regForm.schoolName : null,
      );
      track("sign_up", { method: "password", account_type: regForm.accountType });
      afterAuth(user);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain-surface flex flex-col">
      <header className="max-w-6xl w-full mx-auto px-6 py-6">
        <Link
          to="/"
          className="text-2xl font-extrabold font-display text-secondary"
          data-testid="auth-brand-link"
        >
          {brand.platform_name}
        </Link>
      </header>
      <main className="flex-1 flex items-start justify-center px-6 pb-16 pt-8">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-3xl border border-border shadow-sm p-8">
            <h1 className="text-2xl font-extrabold font-display mb-1">
              Welcome
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              The adult area — for parents and guardians.
            </p>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl gap-3 text-base"
              onClick={loginWithGoogle}
              data-testid="google-login-button"
            >
              <GoogleIcon /> Continue with Google
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-xl gap-3 text-base mt-3"
              onClick={() => setShowWaitlist(!showWaitlist)}
              data-testid="school-sso-button"
            >
              <GraduationCap className="h-5 w-5" aria-hidden="true" />
              Sign in with your school account
              <Badge
                variant="secondary"
                className="ml-1 text-[10px]"
                data-testid="school-sso-coming-soon-badge"
              >
                Coming soon
              </Badge>
            </Button>
            <p
              className="text-xs text-muted-foreground text-center mt-2"
              data-testid="school-sso-note"
            >
              SAML · Clever · ClassLink — district sign-in is on the way.{" "}
              <button
                type="button"
                className="underline font-semibold"
                onClick={() => setShowWaitlist(!showWaitlist)}
                data-testid="school-waitlist-toggle"
              >
                Join the district waitlist
              </button>
            </p>

            {showWaitlist && (
              <form
                onSubmit={joinWaitlist}
                className="mt-3 rounded-xl border border-border bg-muted/30 p-4 space-y-2"
                data-testid="school-waitlist-form"
              >
                {waitlistDone ? (
                  <p
                    className="text-sm font-semibold text-secondary"
                    data-testid="school-waitlist-done"
                  >
                    You're on the list! We'll reach out as soon as district
                    sign-in opens.
                  </p>
                ) : (
                  <>
                    <p className="text-xs font-semibold text-muted-foreground">
                      Leave your details and we'll contact you when school
                      sign-in launches.
                    </p>
                    <Input
                      required
                      minLength={2}
                      maxLength={120}
                      placeholder="District or school name"
                      value={waitlist.district_name}
                      onChange={(e) =>
                        setWaitlist({
                          ...waitlist,
                          district_name: e.target.value,
                        })
                      }
                      className="h-10"
                      data-testid="school-waitlist-district-input"
                    />
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Input
                        required
                        minLength={2}
                        maxLength={80}
                        placeholder="Your name"
                        value={waitlist.contact_name}
                        onChange={(e) =>
                          setWaitlist({
                            ...waitlist,
                            contact_name: e.target.value,
                          })
                        }
                        className="h-10"
                        data-testid="school-waitlist-name-input"
                      />
                      <Input
                        required
                        type="email"
                        placeholder="Work email"
                        value={waitlist.contact_email}
                        onChange={(e) =>
                          setWaitlist({
                            ...waitlist,
                            contact_email: e.target.value,
                          })
                        }
                        className="h-10"
                        data-testid="school-waitlist-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full rounded-xl h-10"
                      disabled={waitlistBusy}
                      data-testid="school-waitlist-submit"
                    >
                      {waitlistBusy ? "Joining…" : "Join the waitlist"}
                    </Button>
                  </>
                )}
              </form>
            )}

            <div className="flex items-center gap-3 my-6" aria-hidden="true">
              <span className="h-px flex-1 bg-border"></span>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                or with email
              </span>
              <span className="h-px flex-1 bg-border"></span>
            </div>

            <Tabs defaultValue={defaultTab}>
              <TabsList className="grid grid-cols-2 w-full mb-6">
                <TabsTrigger value="login" data-testid="auth-login-tab">
                  Sign in
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="auth-register-tab">
                  Create account
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      required
                      autoComplete="email"
                      value={loginForm.email}
                      onChange={(e) =>
                        setLoginForm({ ...loginForm, email: e.target.value })
                      }
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      required
                      autoComplete="current-password"
                      value={loginForm.password}
                      onChange={(e) =>
                        setLoginForm({ ...loginForm, password: e.target.value })
                      }
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-xl text-base"
                    data-testid="login-submit-button"
                  >
                    {loading ? "Signing in…" : "Sign in"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div
                    className="grid grid-cols-2 gap-2"
                    role="radiogroup"
                    aria-label="Account type"
                  >
                    {[
                      { v: "parent", label: "Parent / Guardian" },
                      { v: "professional", label: "Speech Professional" },
                      { v: "teacher", label: "Teacher" },
                      { v: "school_admin", label: "School Administrator" },
                      { v: "creator", label: "Content Creator" },
                    ].map((t) => (
                      <button
                        key={t.v}
                        type="button"
                        role="radio"
                        aria-checked={regForm.accountType === t.v}
                        onClick={() =>
                          setRegForm({ ...regForm, accountType: t.v })
                        }
                        className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${regForm.accountType === t.v ? "bg-secondary text-secondary-foreground border-secondary" : "bg-card border-border hover:bg-accent"}`}
                        data-testid={`register-type-${t.v}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {regForm.accountType === "school_admin" && (
                    <div className="space-y-2">
                      <Label htmlFor="reg-school">School name</Label>
                      <Input
                        id="reg-school"
                        required
                        maxLength={120}
                        value={regForm.schoolName}
                        onChange={(e) =>
                          setRegForm({ ...regForm, schoolName: e.target.value })
                        }
                        data-testid="register-school-name-input"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="reg-name">Your name</Label>
                    <Input
                      id="reg-name"
                      required
                      maxLength={120}
                      value={regForm.name}
                      onChange={(e) =>
                        setRegForm({ ...regForm, name: e.target.value })
                      }
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      required
                      autoComplete="email"
                      value={regForm.email}
                      onChange={(e) =>
                        setRegForm({ ...regForm, email: e.target.value })
                      }
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">
                      Password (8+ characters)
                    </Label>
                    <Input
                      id="reg-password"
                      type="password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      value={regForm.password}
                      onChange={(e) =>
                        setRegForm({ ...regForm, password: e.target.value })
                      }
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="flex items-start gap-3 rounded-xl bg-accent p-4">
                    <Checkbox
                      id="reg-attestation"
                      checked={regForm.attestation}
                      onCheckedChange={(v) =>
                        setRegForm({ ...regForm, attestation: Boolean(v) })
                      }
                      data-testid="register-attestation-checkbox"
                    />
                    <Label
                      htmlFor="reg-attestation"
                      className="text-sm leading-snug cursor-pointer"
                    >
                      {regForm.accountType === "professional"
                        ? "I confirm that I am 18 or older and a practicing speech & language professional. I understand access is granted only by families and is limited to progress data."
                        : regForm.accountType === "teacher" ||
                            regForm.accountType === "school_admin"
                          ? "I confirm that I am 18 or older and a school staff member. I understand child access requires an explicit, revocable parent link and is limited to participation counts."
                          : regForm.accountType === "creator"
                            ? "I confirm that I am 18 or older. I understand my submissions stay quarantined until they pass review, and are never delivered to children during the pilot."
                            : "I confirm that I am 18 or older and the parent or legal guardian of any child I add."}
                    </Label>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !regForm.attestation}
                    className="w-full h-12 rounded-xl text-base"
                    data-testid="register-submit-button"
                  >
                    {loading ? "Creating account…" : "Create account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            <p className="text-xs text-muted-foreground text-center mt-6">
              By continuing you agree to our{" "}
              <Link
                to="/terms"
                className="underline"
                data-testid="auth-terms-link"
              >
                Terms
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                className="underline"
                data-testid="auth-privacy-link"
              >
                Privacy Policy
              </Link>{" "}
              (COPPA-compliant).
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
